import { DateRange } from '@mui/x-date-pickers-pro/DateRangePicker';
import { GridApi, GridReadyEvent, PaginationChangedEvent, CellFocusedEvent, FirstDataRenderedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { iTableSwitch } from '../table-filters/table-filters';
import { RadioFilterOpts } from '../simple-table/simple-table';
import { TableProductsFilterProps } from '../table-products-filter/table-products-filter';
import { AG_GRID_LOCALE_NBNO } from '../simple-table/ag-grid.locale.nb';
import { AG_GRID_LOCALE_EN } from '../simple-table/ag-grid.locale.en';

/* eslint-disable-next-line */
export interface BaseTableProps {
  data: any[],
  headers: any[],
  gridApi: GridApi | null,
  setGridApi: (gridApi: GridApi | null) => void,
  dateRange?: DateRange<Date>,
  onSetDateRange?: (dateRange: DateRange<Date> | null) => void,
  onSelectionChanged?: (...args: any[]) => void,
  onRowClicked?: (event: any) => void;
  onFirstDataRendered?: (event: FirstDataRenderedEvent) => void;
  pageSize?: number,
  disableSearch?: boolean,
  tableFilters?: iTableSwitch[],
  toggleFilters?: RadioFilterOpts,
  expandComponent?: any,
  expandProps?: any,
  productsFilter?: TableProductsFilterProps
  isLoading?: boolean;
  hidePagination?: boolean,
  useCustomPagination?: boolean,
  singleCellSelection?: boolean,
  onRefresh?: () => void;
  onSelectAll?: (...args: any[]) => void,
  getContextMenuItems?: (params: any) => any[],
  onTableFilterChanged?: (params: any) => void,
  onGridPaginationChanged?: (params: PaginationChangedEvent) => void,
  onCellFocused?: (params: CellFocusedEvent) => void,
  summaryRow?: any[],
}
export function BaseTable(props: BaseTableProps) {
  if (props.headers.length === 0) return null;
  return <BaseTableContent {...props} />;
}
function BaseTableContent(props: BaseTableProps) {
  const { t, i18n } = useTranslation();
  const gridApiRef = useRef<GridApi | null>(null);
  const setGridApiRef = useRef(props.setGridApi);
  setGridApiRef.current = props.setGridApi;

  useLayoutEffect(() => () => {
    gridApiRef.current = null;
    setGridApiRef.current(null);
  }, []);

  const updateLoadingOverlay = useCallback(() => {
    const api = gridApiRef.current;
    if (api) {
      props.isLoading ? api.showLoadingOverlay() : api.hideOverlay();
    }
  }, [props.isLoading]);

  useEffect(updateLoadingOverlay, [updateLoadingOverlay]);

  const columnDefinition = {
    resizable: true,
    sortable: true,
  }

  const onGridReady = (params: GridReadyEvent) => {
    gridApiRef.current = params.api;
    props.setGridApi(params.api);
    updateLoadingOverlay();
  };

  return (
    <div className="ag-theme-alpine" data-testid="ag-theme-alpine-base-table">
      <AgGridReact
        localeText={i18n.language === 'nb' ? AG_GRID_LOCALE_NBNO : AG_GRID_LOCALE_EN}
        rowData={props.data}
        columnDefs={props.headers}
        getContextMenuItems={props.getContextMenuItems}
        onFilterChanged={props.onTableFilterChanged}
        onGridReady={onGridReady}
        onFirstDataRendered={props.onFirstDataRendered}
        rowSelection={props.singleCellSelection ? 'false' : 'single'}
        onSelectionChanged={props.onSelectionChanged}
        onRowClicked={props.onRowClicked}
        onCellFocused={props.onCellFocused}
        rowHeight={50}
        domLayout={'autoHeight'}
        defaultColDef={columnDefinition}
        pagination={!props.hidePagination}
        suppressPaginationPanel={props.useCustomPagination}
        onPaginationChanged={props.onGridPaginationChanged}
        masterDetail={true}
        pinnedBottomRowData={props.headers.some(h => h.summary) ? props.summaryRow : undefined}
        paginationPageSize={props.pageSize}
        detailCellRenderer={props.expandComponent}
        detailCellRendererParams={props.expandProps}
        detailRowAutoHeight={true}
        suppressPropertyNamesCheck={true}
      />
    </div>
  );
}

export default BaseTable;